import React, { useCallback, useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoEye, IoTrash, IoVideocam } from "react-icons/io5";
import Main from "../../components/layout/Main";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import { apiMessage } from "../../api/apiMessage";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { CAMERA_PATHS } from "./paths";
import { EzvizError, codesOf, deviceNameOf, listEzvizCameras, succeeded, viewerUrl } from "./cameraUtils";
import "../../assets/styles/app-camera.css";

const DELETE_FAILED = "Couldn't delete the camera. Please try again.";

// One camera device's cameras (app/screens/cam/CameraList.js): EZVIZ's camera
// list for its serial, and the home's verification code for it from the API.
// A camera opens the hosted viewer; owners delete the device after a confirm.
// Location state: { device: an EZVIZ device, accessToken, area }.
function CameraList() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const role = usePropertyRole();
  const device = state?.device;
  const accessToken = state?.accessToken;
  const area = state?.area;
  const serial = device?.deviceSerial;
  // null while loading; { cameras, code } | { error }
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!serial || !accessToken) return;
    setResult(null);
    try {
      const [cameras, codes] = await Promise.all([
        listEzvizCameras(accessToken, serial, area),
        UserService.getHIKCodes(User.getPropertyId()).then(codesOf),
      ]);
      if (alive.current) setResult({ cameras, code: codes[serial] ?? "" });
    } catch (error) {
      if (!alive.current) return;
      setResult({
        error: error instanceof EzvizError ? `EZVIZ: ${error.message}` : "Couldn't load the cameras. Please try again.",
      });
    }
  }, [serial, accessToken, area, alive]);

  useEffect(() => {
    load();
  }, [load]);

  if (!serial || !accessToken) return <Redirect to={CAMERA_PATHS.LIST} />;

  const name = deviceNameOf(device);

  const openCamera = (camera, index) =>
    history.push(CAMERA_PATHS.VIEW, {
      url: viewerUrl(accessToken, serial, result.code, area, camera.channelNo ?? index + 1),
      title: camera.channelName || `Cam ${index + 1}`,
    });

  const askDelete = () => {
    if (role !== "owner") {
      toast("You have to be a owner to delete the camera.", "red");
      return;
    }
    setConfirming(true);
  };

  const deleteConfirmed = async () => {
    setConfirming(false);
    setDeleting(true);
    let response;
    try {
      response = await UserService.deleteHIKDevice(User.getPropertyId(), serial);
    } catch (error) {
      if (!alive.current) return;
      setDeleting(false);
      toast(apiMessage(error?.msg ? { msg: error.msg } : null, DELETE_FAILED), "red");
      return;
    }
    if (!alive.current) return;
    setDeleting(false);
    if (!succeeded(response)) {
      toast(apiMessage({ msg: response?.msg }, DELETE_FAILED), "red");
      return;
    }
    toast(response?.msg || "Camera deleted", "green");
    history.goBack();
  };

  let content;
  if (!result) {
    content = (
      <div className="hx-cam-cards" aria-label="Loading">
        {[0, 1].map((i) => (
          <div key={i} className="hx-skel hx-cam-card-skel" />
        ))}
      </div>
    );
  } else if (result.error) {
    content = (
      <div className="hx-empty" role="alert">
        {result.error}
        <button type="button" className="hx-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  } else if (result.cameras.length) {
    content = (
      <div className="hx-cam-cards">
        {result.cameras.map((camera, index) => {
          const label = `Cam ${index + 1}`;
          return (
            <button
              key={`${camera.channelNo ?? index}`}
              type="button"
              className="hx-cam-card"
              onClick={() => openCamera(camera, index)}
              aria-label={`${camera.channelName || label}. Open camera`}
            >
              <IoEye aria-hidden="true" />
              <span className="hx-cam-card-info">Click to open camera</span>
              {camera.channelName ? <span className="hx-cam-card-channel">{camera.channelName}</span> : null}
              <span className="hx-cam-card-name">{label}</span>
            </button>
          );
        })}
      </div>
    );
  } else {
    content = (
      <div className="hx-empty-page">
        <IoVideocam aria-hidden="true" />
        <p>No cameras on this device</p>
      </div>
    );
  }

  return (
    <Main
      header="stack"
      title={name}
      hideTabBar
      headerRight={
        <button
          type="button"
          className="hx-icon-btn hx-cam-delete"
          onClick={askDelete}
          disabled={deleting}
          aria-label="Delete camera"
        >
          <IoTrash />
        </button>
      }
    >
      <h2 className="hx-section-title">Cameras</h2>
      {content}
      <AlertDialog
        open={confirming}
        type="confirmation"
        title="Delete device"
        message={`Delete ${name} from this home? Are you sure?`}
        confirmLabel="Delete"
        onClose={() => setConfirming(false)}
        onConfirm={deleteConfirmed}
      />
    </Main>
  );
}

export default CameraList;
